import {
  CheckboxField,
  FormElementContainer,
  FormGroupTitle,
  ImgElement,
  type ObjectSchemaDefinition,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { Field, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { IdentityProvider } from '../../../../../generated/id-service-env-administration';
import googleDefault from '../ProviderField/defaultImages/google_logo.svg';
import imageNotFound from '../ProviderField/defaultImages/image_not_found.svg';
import azureDefault from '../ProviderField/defaultImages/microsoft_logo.svg';
import classes from './CustomIdpBranding.module.scss';

export interface CustomBrandingConfig {
  idpId?: IdentityProvider;
  idpImageUrl?: string | null;
  displayName?: string | null;
  useCustomBranding?: boolean;
}

export const customBrandingValidation =
  Yup.object<ObjectSchemaDefinition>().shape({
    displayName: Yup.string()
      .max(200)
      .required('Display name is required when using custom branding.'),
    idpImageUrl: Yup.string()
      .max(1000)
      .required('IDP image URL is required when using custom branding.'),
  });

interface CustomIdpBrandingProps {
  isEnabledByDefault?: boolean;
}

export const CustomIdpBranding: React.FC<CustomIdpBrandingProps> = ({
  isEnabledByDefault = false,
}) => {
  const { values, setFieldValue } = useFormikContext<CustomBrandingConfig>();
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

  useEffect(() => {
    switch (values.idpImageUrl) {
      case 'DEFAULT_AZURE':
        setPreviewImageUrl(azureDefault);
        break;
      case 'DEFAULT_GOOGLE':
        setPreviewImageUrl(googleDefault);
        break;
      default:
        setPreviewImageUrl(values.idpImageUrl ?? '');
        break;
    }
  }, [values.idpImageUrl]);

  useEffect(() => {
    if (values.useCustomBranding === false) {
      switch (values.idpId) {
        case IdentityProvider.AzureAd:
          setFieldValue('idpImageUrl', 'DEFAULT_AZURE', false);
          setFieldValue('displayName', 'Sign In with Microsoft', false);
          break;
        case IdentityProvider.Google:
          setFieldValue('idpImageUrl', 'DEFAULT_GOOGLE', false);
          setFieldValue('displayName', 'Sign In with Google', false);
          break;
        default:
          setFieldValue('idpImageUrl', null, false);
          setFieldValue('displayName', null, false);
          break;
      }
    }
  }, [setFieldValue, values.idpId, values.useCustomBranding]);

  return (
    <>
      <FormGroupTitle title="Appearance" />
      {!isEnabledByDefault && (
        <Field
          name="useCustomBranding"
          label="Use Custom Branding"
          as={CheckboxField}
        />
      )}

      <Field
        name="displayName"
        label="Display Name"
        autoComplete="off"
        disabled={!values.useCustomBranding}
        as={SingleLineTextField}
        value={values.displayName}
      />
      <Field
        name="idpImageUrl"
        label="Image URL"
        autoComplete="off"
        tooltipContent="URL to the image that will be displayed on the button. It is recommended that the image have a 1:1 ratio."
        disabled={!values.useCustomBranding}
        value={values.idpImageUrl}
        as={SingleLineTextField}
      />
      <FormElementContainer label="Button Preview">
        <div className={classes.button_idp_container}>
          <div className={classes.button_idp}>
            <ImgElement
              src={previewImageUrl}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = imageNotFound;
              }}
              decorative={true}
            />
            {values.displayName}
          </div>
        </div>
      </FormElementContainer>
    </>
  );
};
